/**
 * GA4 event helper.
 *
 * ── Why this file had to exist ────────────────────────────────────────────────
 * GA4 was installed and correctly scoped (components/GoogleAnalytics.tsx) and
 * sent `page_view` and nothing else. So Search Console could show which queries
 * produced clicks, GA4 could show which pages produced sessions, and nothing
 * anywhere joined either of them to an actual enquiry. The contact form's
 * success state is an inline panel rather than a /thank-you URL, so there was
 * not even a destination to count — which means the only way to measure the
 * one thing the site exists to do is an explicit event.
 *
 * Two rules, both enforced here rather than at the call sites:
 *  1. Never throw. A blocked tag, a privacy extension or a GA outage must not
 *     break a form submit. Measurement is strictly subordinate to the thing
 *     being measured.
 *  2. Never send tenant data. `gtag` is only ever live on public marketing
 *     pages (see GoogleAnalytics.tsx for the reasoning), and event parameters
 *     here must stay the same shape: page paths and short enum-like strings.
 *     No names, no emails, no phone numbers — a lead's identity belongs in our
 *     own database and nowhere else.
 */

declare global {
  interface Window {
    // Declared in GoogleAnalytics.tsx too; identical signature so the two merge.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
  }
}

/** Fire a GA4 event. Silent no-op when the tag is absent or disabled. */
export function trackEvent(name: string, params: Record<string, string | number | boolean> = {}): void {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('event', name, params);
  } catch {
    /* see rule 1 */
  }
}

/**
 * The one conversion that matters: somebody asked us to contact them.
 *
 * `generate_lead` is GA4's own recommended event name rather than a custom one,
 * so it lands in the standard reports and can be marked a key event in the UI
 * without any extra configuration. Mark it in Admin → Events → Key events, and
 * link the property to Search Console (Admin → Product links) — that pairing is
 * what turns "query → landing page → lead" into a single report.
 *
 * `lead_landing_page` is the FIRST page of the visit, not the form's URL.
 * Reporting '/contact' as the landing page of every lead is the GA4 equivalent
 * of the bug this release fixed in our own database; see lib/attribution.ts.
 */
export function trackLead(params: {
  interest: string;
  sites: string;
  /**
   * Named `lead_landing_page`, not `landing_page`.
   *
   * GA4 already has a built-in session-scoped dimension called "Landing page".
   * Registering a custom dimension with the same name is at best confusing in
   * the UI and at worst rejected, and the two mean different things here: the
   * built-in is the session's entry page, this is the entry page of the visit
   * that converted. The prefix keeps them distinguishable in a report.
   */
  lead_landing_page: string;
  cta_from: string;
  /** Asset slug when the lead came from a guide download, '' otherwise. */
  asset: string;
  /** 'en' | 'ta' — which language the form was filled in. */
  lang: string;
}): void {
  trackEvent('generate_lead', {
    ...params,
    // No monetary value is sent. GA4 will happily report a currency figure as
    // revenue, and a made-up per-lead value would turn into a chart somebody
    // later treats as a fact.
    form: 'contact',
  });
}
